import React, { useEffect, useContext, useState } from "react";
import styled from "styled-components";
import xlsx from "xlsx";
import DropZone from "Components/Common/DropZone";
import GlobalContext from "Hooks/globalContext";
import { HOST } from "Utils/constants";
import useInput from "Hooks/useInput";
import Axios from "axios";
import DropZoneExcel from "Components/Common/DropZoneExcel";

const ConfigWrapper = styled.div`
  padding: 0 32px 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const GridLayout = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const GridLayout3 = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const SectionCard = styled.div`
  background: rgba(30, 41, 59, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  backdrop-filter: blur(12px);
  overflow: hidden;
`;

const SectionHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const SectionTitle = styled.h3`
  color: #f1f5f9;
  font-size: 15px;
  font-weight: 600;
  margin: 0;
`;

const SectionSub = styled.p`
  color: #64748b;
  font-size: 12px;
  margin: 4px 0 0;
`;

const SectionContent = styled.div`
  padding: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const FormLabel = styled.label`
  display: block;
  color: #94a3b8;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
`;

const HelperText = styled.p`
  color: #64748b;
  font-size: 11px;
  margin: 4px 0 0;
`;

const StyledInput = styled.input`
  width: 100%;
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 10px 14px;
  color: #f1f5f9;
  font-size: 13px;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
  &:focus {
    border-color: #3d73ad;
    box-shadow: 0 0 0 3px rgba(61, 115, 173, 0.15);
  }
  &::placeholder {
    color: #64748b;
  }
`;

const PrimaryButton = styled.button`
  background: #3d73ad;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  width: 100%;
  margin-top: 8px;
  &:hover {
    background: #2a5a8f;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DangerButton = styled(PrimaryButton)`
  background: transparent;
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #ef4444;
  &:hover {
    background: rgba(239, 68, 68, 0.1);
  }
`;

const ManageButton = styled.button`
  background: rgba(61, 115, 173, 0.15);
  color: #5b8fbf;
  border: 1px solid rgba(61, 115, 173, 0.25);
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  padding: 3px 10px;
  cursor: pointer;
  transition: background 0.15s;
  &:hover {
    background: rgba(61, 115, 173, 0.25);
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  margin: 12px 0;
`;

const CoinTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  th {
    text-align: left;
    padding: 10px 16px;
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    background: rgba(15, 23, 42, 0.4);
  }
  td {
    padding: 12px 16px;
    color: #e2e8f0;
    font-size: 13px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  }
  tr:hover td {
    background: rgba(61, 115, 173, 0.06);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #1e293b;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 0;
  width: 480px;
  max-width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h3`
  color: #f1f5f9;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const ModalFooter = styled.div`
  padding: 16px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #64748b;
  font-size: 20px;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  &:hover {
    color: #94a3b8;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 8px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #94a3b8;
  font-size: 13px;
  cursor: pointer;
  input[type="radio"] {
    accent-color: #3d73ad;
  }
`;

const CurrencyNote = styled.p`
  color: #64748b;
  font-size: 11px;
  margin: 4px 0 0;
`;

const Page = ({ adminPage, focus }) => {
  const { httpHeader, web3, modal } = useContext(GlobalContext);
  const [config, setConfig] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [single, setSingle] = useState(null);
  const [backdrop, setBackdrop] = useState(null);
  const [currencyType, setCurrencyType] = useState("dollar");

  const [excel, setExcel] = useState(null);

  const coinName = useInput({ defaultValue: "" });
  const coinTicker = useInput({ defaultValue: "" });
  const coinContract = useInput({ defaultValue: "", maxLength: 43 });
  const defaultMarketcap = useInput({ defaultValue: "" });

  const stakingDay = useInput({ defaultValue: "" });
  const stakingPer = useInput({ defaultValue: "" });
  const stakingMin = useInput({ defaultValue: "" });
  const stakingAvailable = useInput({ defaultValue: "" });
  const payFee = useInput({ defaultValue: "" });

  const deleteCheck = useInput({ defaultValue: "" });

  const defaultMarketcapUpdate = useInput({ defaultValue: "" });

  const [singleUpdate, setSingleUpdate] = useState(null);
  const [token, setToken] = useState(null);

  const preload = () => {
    getConfig();
  };

  const onSubmitUpdate = async (e) => {
    e.preventDefault();

    if (isNaN(Number(defaultMarketcapUpdate.value))) {
      return modal.setState("올바른 입력이 아닙니다.");
    }

    const frm = new FormData();
    frm.append("name", dialog.name);
    frm.append("ticker", dialog.ticker);
    frm.append("currencyType", currencyType);
    frm.append("defaultMarketcap", defaultMarketcapUpdate.value);
    frm.append("image", singleUpdate);

    const _header = httpHeader();
    _header.headers["Content-Type"] = "multipart/form-data";
    setBackdrop(true);
    const { data } = await Axios.post(`${HOST}/admin/coin/update`, frm, _header);
    getConfig();
    coinName.setValue("");
    coinTicker.setValue("");
    coinContract.setValue("");
    defaultMarketcapUpdate.setValue("");
    setSingleUpdate(null);
    setBackdrop(false);
  };

  const onSubmitAddCoin = async (e) => {
    e.preventDefault();
    if (!single) {
      return modal.setState("심볼 이미지 파일을 등록해주세요.");
    }
    if (coinName.value === "") {
      return modal.setState("코인 이름을 입력해주세요.");
    }
    if (coinTicker.value === "") {
      return modal.setState("코인 티커를 입력해주세요");
    }
    if (coinContract.value.length !== 42 || coinContract.value === "") {
      return modal.setState("올바른 컨트랙트 주소를 입력해주세요.");
    }

    if (defaultMarketcap.value === "") {
      return modal.setState("기본 설정 마켓캡이 등록되지 않았습니다.");
    }

    if (isNaN(Number(defaultMarketcap.value))) {
      return modal.setState("올바른 입력이 아닙니다.");
    }

    const frm = new FormData();
    frm.append("currencyType", currencyType);
    frm.append("name", coinName.value);
    frm.append("ticker", coinTicker.value);
    frm.append("contract", coinContract.value);
    frm.append("defaultMarketcap", defaultMarketcap.value);
    frm.append("image", single);

    const _header = httpHeader();
    _header.headers["Content-Type"] = "multipart/form-data";
    setBackdrop(true);
    const { data } = await Axios.post(`${HOST}/admin/coin/add`, frm, _header);
    getConfig();
    coinName.setValue("");
    coinTicker.setValue("");
    coinContract.setValue("");
    defaultMarketcap.setValue("");
    setSingle(null);
    setBackdrop(false);
  };

  const getConfig = async () => {
    setBackdrop(true);
    try {
      const { data } = await Axios.post(`${HOST}/admin/setting/app`, {}, httpHeader());
      stakingDay.setValue(data.config.staking_day);
      stakingPer.setValue(data.config.staking_per);
      stakingMin.setValue(data.config.staking_min);
      stakingAvailable.setValue(data.config.staking_available);
      payFee.setValue(data.config.pay_fee);
      setToken(data.token);
    } catch (e) {
      console.error("getConfig error:", e);
      setToken([]);
    }
    setBackdrop(false);
  };

  const onSubmitConfig = async (e) => {
    e.preventDefault();
    if (
      isNaN(Number(stakingDay.value)) ||
      isNaN(Number(stakingPer.value)) ||
      isNaN(Number(stakingMin.value)) ||
      isNaN(Number(payFee.value)) ||
      isNaN(Number(stakingAvailable.value))
    ) {
      return modal.setState("잘못된 입력입니다.");
    }
    if (Number(stakingPer.value) > 100 || Number(stakingPer.value) < 0) {
      return modal.setState("잘못된 입력입니다.");
    }
    if (Number(payFee.value) > 100 || Number(payFee.value) < 0) {
      return modal.setState("잘못된 입력입니다.");
    }
    if (Number(stakingDay.value) < 0) {
      return modal.setState("잘못된 입력입니다.");
    }
    if (Number(stakingPer.value) < 0) {
      return modal.setState("잘못된 입력입니다.");
    }
    if (Number(stakingMin.value) < 0) {
      return modal.setState("잘못된 입력입니다.");
    }
    if (Number(stakingAvailable.value) < 0) {
      return modal.setState("잘못된 입력입니다.");
    }
    if (Number(stakingAvailable.value) > 1) {
      return modal.setState("잘못된 입력입니다.");
    }

    const { data } = await Axios.post(
      `${HOST}/admin/setting/app/save`,
      {
        stakingDay: stakingDay.value,
        stakingPer: stakingPer.value,
        stakingMin: stakingMin.value,
        payFee: payFee.value,
        stakingAvailable: stakingAvailable.value,
      },
      httpHeader()
    );
    getConfig();
    modal.setState("저장되었습니다.");
  };

  const blobToXlsx = () => {
    const workbook = xlsx.read(excel.binary, { type: "binary" }).Sheets;
    const content = workbook["Sample"];
    const result = [];
    for (let key in content) {
      const tag = key.substring(0, 1);

      const num = Number(key.substring(1, key.length));
      if (num > 1) {
        switch (tag) {
          case "A":
            result[num] = {
              ...result[num],
              email: content[key].v,
            };
            break;
          case "B":
            result[num] = {
              ...result[num],
              name: content[key].v,
            };
            break;
          case "C":
            result[num] = {
              ...result[num],
              date: content[key].v,
            };
            break;
          case "D":
            result[num] = {
              ...result[num],
              phone: content[key].v,
            };
            break;
          case "E":
            result[num] = {
              ...result[num],
              pin: content[key].v,
            };
            break;
        }
      }
    }
    return result.filter(function (el) {
      return el != null;
    });
  };

  const onSubmitExcel = async (e) => {
    e.preventDefault();
    const result = blobToXlsx();
    const { data } = await Axios.post(`${HOST}/admin/excel/register`, result, httpHeader());

    if (data.status) {
      setExcel(null);
      modal.setState("가입링크가 전송되었습니다.");
    }
  };

  useEffect(preload, []);

  return (
    <ConfigWrapper>
      {dialog && (
        <ModalOverlay onClick={() => setDialog(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>코인 변경</ModalTitle>
              <CloseButton onClick={() => setDialog(null)}>×</CloseButton>
            </ModalHeader>
            <ModalBody>
              <form onSubmit={onSubmitUpdate}>
                <FormGroup>
                  <FormLabel>기본 설정 마켓캡 ($)</FormLabel>
                  <StyledInput placeholder="기본 설정 마켓캡 ($)" {...defaultMarketcapUpdate} />
                </FormGroup>
                <FormGroup>
                  <FormLabel>가격 표기 방법</FormLabel>
                  <CurrencyNote>(원화 선택시 달러 가격에서 원화로 환산된 금액으로 표기됨)</CurrencyNote>
                  <RadioGroup>
                    <RadioLabel>
                      <input
                        type="radio"
                        name="currencyType-update"
                        value="dollar"
                        checked={currencyType === "dollar"}
                        onChange={({ target: { value } }) => setCurrencyType(value)}
                      />
                      달러
                    </RadioLabel>
                    <RadioLabel>
                      <input
                        type="radio"
                        name="currencyType-update"
                        value="won"
                        checked={currencyType === "won"}
                        onChange={({ target: { value } }) => setCurrencyType(value)}
                      />
                      원화
                    </RadioLabel>
                  </RadioGroup>
                </FormGroup>
                <DropZone single={singleUpdate} setSingle={setSingleUpdate} />
                <FormGroup>
                  <FormLabel>삭제하실 경우에만 '삭제'를 입력해주세요.</FormLabel>
                  <StyledInput placeholder="삭제" {...deleteCheck} />
                </FormGroup>
                <PrimaryButton type="submit">수정</PrimaryButton>
                <DangerButton
                  type="button"
                  onClick={async () => {
                    if (deleteCheck.value !== "삭제") {
                      return modal.setState("입력창에 '삭제' 를 입력해주세요. (실수방지)");
                    }

                    const { data } = await Axios.post(
                      `${HOST}/admin/coin/delete`,
                      { name: dialog.name, ticker: dialog.ticker },
                      httpHeader()
                    );
                    getConfig();
                    setDialog(null);
                  }}
                >
                  삭제
                </DangerButton>
              </form>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      <GridLayout3>
        <SectionCard>
          <SectionHeader>
            <SectionTitle>코인 추가</SectionTitle>
          </SectionHeader>
          <SectionContent>
            <form onSubmit={onSubmitAddCoin}>
              <FormGroup>
                <FormLabel>코인 풀 네임 (e.g. BitCoin)</FormLabel>
                <StyledInput placeholder="코인 풀 네임 (e.g. BitCoin)" {...coinName} />
              </FormGroup>
              <FormGroup>
                <FormLabel>코인 티커 (e.g. BTC)</FormLabel>
                <StyledInput placeholder="코인 티커 (e.g. BTC)" {...coinTicker} />
              </FormGroup>
              <FormGroup>
                <FormLabel>컨트랙트 주소</FormLabel>
                <StyledInput placeholder="컨트랙트 주소" {...coinContract} />
              </FormGroup>
              <FormGroup>
                <FormLabel>기본 설정 마켓캡 ($)</FormLabel>
                <StyledInput placeholder="기본 설정 마켓캡 ($)" {...defaultMarketcap} />
              </FormGroup>
              <FormGroup>
                <FormLabel>가격 표기 방법</FormLabel>
                <CurrencyNote>(원화 선택시 달러 가격에서 원화로 환산된 금액으로 표기됨)</CurrencyNote>
                <RadioGroup>
                  <RadioLabel>
                    <input
                      type="radio"
                      name="currencyType-add"
                      value="dollar"
                      checked={currencyType === "dollar"}
                      onChange={({ target: { value } }) => setCurrencyType(value)}
                    />
                    달러
                  </RadioLabel>
                  <RadioLabel>
                    <input
                      type="radio"
                      name="currencyType-add"
                      value="won"
                      checked={currencyType === "won"}
                      onChange={({ target: { value } }) => setCurrencyType(value)}
                    />
                    원화
                  </RadioLabel>
                </RadioGroup>
              </FormGroup>
              <DropZone single={single} setSingle={setSingle} />
              <Divider />
              <PrimaryButton type="submit">코인 추가</PrimaryButton>
            </form>
          </SectionContent>
        </SectionCard>

        <SectionCard>
          <SectionHeader>
            <SectionTitle>설정 변경</SectionTitle>
          </SectionHeader>
          <SectionContent>
            <form onSubmit={onSubmitConfig}>
              <FormGroup>
                <FormLabel>가맹점 결제 수수료 (%)</FormLabel>
                <StyledInput placeholder="가맹점 결제 수수료 (%)" {...payFee} />
              </FormGroup>
              <FormGroup>
                <FormLabel>스테이킹 일(Day)수</FormLabel>
                <StyledInput placeholder="스테이킹 일(Day)수" {...stakingDay} />
              </FormGroup>
              <FormGroup>
                <FormLabel>스테이킹 보상 %</FormLabel>
                <StyledInput placeholder="스테이킹 보상 %" {...stakingPer} />
              </FormGroup>
              <FormGroup>
                <FormLabel>최소 스테이킹 수량</FormLabel>
                <StyledInput placeholder="최소 스테이킹 수량" {...stakingMin} />
              </FormGroup>
              <FormGroup>
                <FormLabel>스테이킹 상태</FormLabel>
                <StyledInput placeholder="스테이킹 상태" {...stakingAvailable} />
                <HelperText>스테이킹 상태, 1=스테이킹가능, 0=스테이킹불가</HelperText>
              </FormGroup>
              <Divider />
              <PrimaryButton type="submit">설정 저장</PrimaryButton>
            </form>
          </SectionContent>
        </SectionCard>
      </GridLayout3>

      <GridLayout3>
        <SectionCard>
          <SectionHeader>
            <SectionTitle>코인 관리</SectionTitle>
          </SectionHeader>
          <SectionContent style={{ padding: 0 }}>
            <CoinTable>
              <thead>
                <tr>
                  <th>이름</th>
                  <th>티커</th>
                  <th>심볼</th>
                  <th>설정된 마켓캡($)</th>
                  <th>가격 표기 방식</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {(token || []).map(({ name, ticker, symbol_image, default_marketcap, currency_type }, index) => (
                  <tr key={index}>
                    <td>{name}</td>
                    <td>{ticker}</td>
                    <td>
                      <img style={{ width: 24, height: 24 }} src={HOST + "/" + symbol_image} alt={ticker} />
                    </td>
                    <td>{default_marketcap ? Number(default_marketcap) + " $" : " - "}</td>
                    <td>{currency_type}</td>
                    <td>
                      <ManageButton
                        onClick={() => {
                          defaultMarketcapUpdate.setValue(Number(default_marketcap));
                          setDialog({ name, ticker, symbol_image, default_marketcap });
                        }}
                      >
                        관리
                      </ManageButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </CoinTable>
          </SectionContent>
        </SectionCard>

        <SectionCard>
          <SectionHeader>
            <SectionTitle>엑셀 가입링크 보내기</SectionTitle>
          </SectionHeader>
          <SectionContent>
            <form onSubmit={onSubmitExcel}>
              <DropZoneExcel single={excel} setSingle={setExcel} />
              <Divider />
              <PrimaryButton type="submit">엑셀 가입링크 전송</PrimaryButton>
            </form>
          </SectionContent>
        </SectionCard>
      </GridLayout3>
    </ConfigWrapper>
  );
};

export default Page;
